import React, { useState, useEffect } from 'react';
import { Modal, Form, Input, Select, Button, message, Space, DatePicker } from 'antd';
import { MinusCircleOutlined, PlusOutlined } from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from "react-i18next";
import { useGetDealStagesQuery } from '@/redux/rtk/features/CRM/dealStage/dealStageApi';
import { useGetLeadSourcesQuery } from '@/redux/rtk/features/leadSource/leadSourceApi';
import { loadAllStaff } from '@/redux/rtk/features/hrm/user/userSlice';
import { useGetInterestsQuery } from '@/redux/rtk/features/interest/interestApi';
import { useGetMajorsQuery } from '@/redux/rtk/features/major/majorApi';
import { useGetUniversitiesQuery } from '@/redux/rtk/features/university/universityApi';
import { useGetLeadStatusesQuery } from '@/redux/rtk/features/leadStatus/leadStatusApi';
import { useGetDegreesQuery } from '@/redux/rtk/features/degree/degreeApi';
import { useGetContactStatusesQuery } from '@/redux/rtk/features/CRM/contactStatus/contactStatusApi';
import { loadAllAgent } from '@/redux/rtk/features/CRM/agent/agentSlice';
import { loadAllContactStage } from '@/redux/rtk/features/CRM/contactStage/contactStageSlice';
import { saveAnalytics, updateAnalytics } from '@/redux/rtk/features/CRM/analytics/analyticsSlice';
import { COUNTRIES } from '@/utils/countries';
import { NATIONALITIES } from '@/utils/nationalities';

const { Option } = Select;
const { TextArea } = Input;
const { RangePicker } = DatePicker;


// Semantic Layer will be defined inside the component to use translation hook

const CreateAnalyticsModal = ({ visible, onClose, editingAnalytic = null, pageId = null }) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);

    // 1. Semantic Layer: Moved inside component to use translations
    const SEMANTIC_MODELS = {
        deals: {
            label: t('analytics.sources.deals'),
            dimensions: [
                { label: t('analytics.labels.dealOwner'), value: 'dealOwnerId' },
                { label: t('analytics.labels.dealStage'), value: 'dealStageId' },
                { label: t('analytics.labels.contactStatus'), value: 'contactStatusId' },
                { label: t('analytics.labels.agent'), value: 'agent_id' },
                { label: t('analytics.labels.commissionPaid'), value: 'agent_commission_paid' },
                { label: t('analytics.labels.degree'), value: 'degree' },
                { label: t('analytics.labels.scholarship'), value: 'scholarship' },
                { label: t('analytics.labels.leadSource'), value: 'leadSourceId' },
                { label: t('analytics.labels.country'), value: 'country' },
                { label: t('analytics.labels.nationality'), value: 'nationality' },
                { label: t('analytics.labels.major'), value: 'major_id' },
                { label: t('analytics.labels.university'), value: 'university_id' },
                { label: t('analytics.labels.createdDate'), value: 'created_at' },
            ],
            metrics: [
                { label: t('analytics.labels.totalDeals'), value: 'count' },
                { label: t('analytics.labels.totalRevenue'), value: 'revenue' },
                { label: t('analytics.labels.averageValue'), value: 'average_value' },
                { label: t('analytics.labels.wonDeals'), value: 'won_count' },
            ]
        },
        leads: {
            label: t('analytics.sources.leads'),
            dimensions: [
                { label: t('analytics.labels.leadOwner'), value: 'leadOwnerId' },
                { label: t('analytics.labels.source'), value: 'leadSourceId' },
                { label: t('analytics.labels.status'), value: 'leadStatus' },
                { label: t('analytics.labels.country'), value: 'country' },
                { label: t('analytics.labels.nationality'), value: 'nationality' },
                { label: t('analytics.labels.interest'), value: 'interest' },
                { label: t('analytics.labels.major'), value: 'major' },
                { label: t('analytics.labels.createdDate'), value: 'created_at' },
            ],
            metrics: [
                { label: t('analytics.labels.totalLeads'), value: 'count' },
            ]
        },
        contacts: {
            label: t('analytics.sources.contacts'),
            dimensions: [
                { label: t('analytics.labels.owner'), value: 'contactOwnerId' },
                { label: t('analytics.labels.stage'), value: 'contactStageId' },
                { label: t('analytics.labels.status'), value: 'contactStatusId' },
                { label: t('analytics.labels.source'), value: 'contactSourceId' },
                { label: t('analytics.labels.country'), value: 'country' },
                { label: t('analytics.labels.city'), value: 'city' },
                { label: t('analytics.labels.createdDate'), value: 'created_at' },
            ],
            metrics: [
                { label: t('analytics.labels.totalContacts'), value: 'count' },
            ]
        },
        applications: {
            label: t('analytics.sources.applications'),
            dimensions: [
                { label: t('analytics.labels.university'), value: 'university_id' },
                { label: t('analytics.labels.major'), value: 'major_id' },
                { label: t('analytics.labels.status'), value: 'status' },
                { label: t('analytics.labels.createdDate'), value: 'created_at' },
            ],
            metrics: [
                { label: t('analytics.labels.totalApplications'), value: 'count' },
            ]
        },
        agents: {
            label: t('analytics.sources.agents'),
            dimensions: [
                { label: t('analytics.labels.agentName'), value: 'id' },
                { label: t('analytics.labels.email'), value: 'email' },
                { label: t('analytics.labels.phone'), value: 'phone' },
                { label: t('analytics.labels.country'), value: 'country' },
                { label: t('analytics.labels.city'), value: 'city' },
                { label: t('analytics.labels.createdDate'), value: 'created_at' },
            ],
            metrics: [
                { label: t('analytics.labels.totalAgents'), value: 'count' },
                { label: t('analytics.labels.totalStudents'), value: 'total_students' },
                { label: t('analytics.labels.commissionsPaid'), value: 'commission_paid' },
            ]
        }
    };

    // Unified State Object
    const [definition, setDefinition] = useState({
        source: 'deals',
        dimensions: [],
        metrics: ['count'],
        filters: [] // Changed to array for multi-filter support: [{ field, operator, value }]
    });

    const isEditMode = !!editingAnalytic;

    // Redux Data
    const { data: dealStages } = useGetDealStagesQuery(undefined, { skip: !visible });
    const { data: contactStatuses } = useGetContactStatusesQuery(undefined, { skip: !visible });
    const { data: leadSourcesData } = useGetLeadSourcesQuery(undefined, { skip: !visible });
    const leadSources = Array.isArray(leadSourcesData?.leadSources) ? leadSourcesData.leadSources : (Array.isArray(leadSourcesData) ? leadSourcesData : []);

    const { data: interestsData } = useGetInterestsQuery(undefined, { skip: !visible });
    const interests = Array.isArray(interestsData?.interests) ? interestsData.interests : (Array.isArray(interestsData) ? interestsData : []);

    const { data: majorsData } = useGetMajorsQuery(undefined, { skip: !visible });
    const majors = Array.isArray(majorsData?.majors) ? majorsData.majors : (Array.isArray(majorsData) ? majorsData : []);

    const { data: universitiesData } = useGetUniversitiesQuery(undefined, { skip: !visible });
    const universities = Array.isArray(universitiesData?.universities) ? universitiesData.universities : (Array.isArray(universitiesData) ? universitiesData : []);

    const { data: leadStatusesData } = useGetLeadStatusesQuery(undefined, { skip: !visible });
    const { data: degrees, isLoading: degreeLoading } = useGetDegreesQuery(undefined, { skip: !visible });
    const leadStatuses = Array.isArray(leadStatusesData) ? leadStatusesData : (leadStatusesData?.leadStatus || []);

    const staffList = useSelector((state) => state.users.list);
    const agentList = useSelector((state) => state.agent.list);
    const contactStageList = useSelector((state) => state.contactStage.list);

    useEffect(() => {
        if (visible) {
            if (!staffList) dispatch(loadAllStaff({ query: 'all' }));
            if (!agentList) dispatch(loadAllAgent());
            if (!contactStageList) dispatch(loadAllContactStage());
        }
    }, [dispatch, visible, staffList, agentList, contactStageList]);

    // Initialize Form
    useEffect(() => {
        if (visible) {
            if (editingAnalytic) {
                // Parse filters
                let parsedFilters = editingAnalytic.filters || {};
                if (typeof parsedFilters === 'string') {
                    try { parsedFilters = JSON.parse(parsedFilters); } catch (e) { }
                }

                // Convert object filters back to array for UI
                const filterArray = Object.entries(parsedFilters).map(([key, value]) => {
                    let fieldName = key;
                    if (key === 'companyId' || key === 'agentId') fieldName = 'agent_id';

                    let formattedValue = value;

                    // Ensure values for multi-select ID fields are Arrays of Numbers
                    // This fixes the issue where Ant Design Select shows the ID instead of the Label due to type mismatch
                    if (fieldName.endsWith('Id') || fieldName.endsWith('_id') || fieldName === 'id') {
                        if (!Array.isArray(value)) {
                            if (typeof value === 'string' && value.includes(',')) {
                                formattedValue = value.split(',').map(v => Number(v));
                            } else {
                                // If it's a single value, wrap in array and convert to number
                                // Use String conversion first to handle potential edge cases, then Number
                                formattedValue = value ? [Number(value)] : [];
                            }
                        } else {
                            // Ensure all elements in array are numbers
                            formattedValue = value.map(v => Number(v));
                        }
                    }

                    return {
                        field: fieldName,
                        operator: 'is', // Defaulting to 'is'/equals for now
                        value: formattedValue
                    };
                });

                const groupBy = editingAnalytic.chartConfig?.groupBy;

                setDefinition({
                    source: editingAnalytic.dataSource || 'deals',
                    dimensions: groupBy ? [groupBy] : [],
                    metrics: editingAnalytic.chartConfig?.aggregation ? [editingAnalytic.chartConfig.aggregation] : ['count'],
                    filters: filterArray
                });

                form.setFieldsValue({
                    name: editingAnalytic.name,
                    description: editingAnalytic.description,
                    dataSource: editingAnalytic.dataSource || 'deals',
                    groupBy: groupBy,
                    aggregation: editingAnalytic.chartConfig?.aggregation || 'count',
                    chartType: editingAnalytic.chartType || 'bar',
                    timePeriod: editingAnalytic.chartConfig?.timePeriod || 'all_time',
                    filters: filterArray // Set form filters
                });
            } else {
                setDefinition({ source: 'deals', dimensions: [], metrics: ['count'], filters: [] });
                form.resetFields();
            }
        }
    }, [visible, editingAnalytic, form]);

    const handleDefinitionChange = (key, value) => {
        setDefinition(prev => {
            const newDef = { ...prev, [key]: value };
            if (key === 'source') {
                newDef.dimensions = [];
                newDef.metrics = ['count'];
                newDef.filters = [];
                form.setFieldsValue({
                    groupBy: undefined,
                    aggregation: 'count',
                    filters: []
                });
            }
            return newDef;
        });
    };

    const handleOk = async () => {
        try {
            const values = await form.validateFields();
            setLoading(true);

            // Construct Filters Object from Array
            const filtersObject = {};
            if (values.filters && values.filters.length > 0) {
                values.filters.forEach(f => {
                    if (f.field && f.value !== undefined && f.value !== null) {
                        // Handle Date Objects -> String
                        if (typeof f.value === 'object' && f.value.$d) {
                            filtersObject[f.field] = f.value.format('YYYY-MM-DD');
                        } else {
                            filtersObject[f.field] = f.value;
                        }
                    }
                });
            }

            const payload = {
                name: values.name,
                description: values.description,
                dataSource: definition.source,
                chartType: values.chartType,
                chartConfig: {
                    groupBy: definition.dimensions[0],
                    aggregation: definition.metrics[0],
                    timePeriod: values.timePeriod,
                    pageId: pageId,
                },
                filters: filtersObject,
                isTemplate: false,
                isPublic: false,
            };

            if (isEditMode) {
                await dispatch(updateAnalytics({ id: editingAnalytic.id, data: payload })).unwrap();
                message.success(t('success.updated'));
            } else {
                await dispatch(saveAnalytics(payload)).unwrap();
                message.success(t('success.created'));
            }

            form.resetFields();
            setDefinition({ source: 'deals', dimensions: [], metrics: ['count'], filters: [] });
            onClose();
        } catch (error) {
            console.error('Failed to save analytics:', error);
            message.error('Failed to save analytics.');
        } finally {
            setLoading(false);
        }
    };

    const getSourceOptions = () => Object.entries(SEMANTIC_MODELS).map(([key, model]) => ({ label: model.label, value: key }));
    const currentModel = SEMANTIC_MODELS[definition.source];
    const dimensionOptions = currentModel?.dimensions || [];
    const metricOptions = currentModel?.metrics || [];

    // Smart Input Renderer for Filters
    const renderFilterInput = (field) => {
        if (!field) return <Input disabled placeholder="Select field first" />;

        // Helper to find generic field name regardless of ID suffix
        const lowerField = field.toLowerCase();

        if (lowerField.includes('owner') || lowerField.includes('user')) {
            return (
                <Select mode="multiple" placeholder={t('analytics.labels.owner')} allowClear showSearch optionFilterProp="children">
                    {staffList?.map(u => (
                        <Option key={u.id} value={u.id}>{`${u.firstName} ${u.lastName}`}</Option>
                    ))}
                </Select>
            );
        }
        if (field === 'dealStageId') {
            return (
                <Select mode="multiple" placeholder="Select Stages" allowClear showSearch optionFilterProp="children">
                    {dealStages?.map(s => <Option key={s.id} value={s.id}>{s.stageName}</Option>)}
                </Select>
            );
        }
        if (field === 'contactStageId') {
            return (
                <Select mode="multiple" placeholder={t('analytics.labels.stage')} allowClear showSearch optionFilterProp="children">
                    {contactStageList?.map(s => <Option key={s.id} value={s.id}>{s.stageName}</Option>)}
                </Select>
            );
        }
        if (field === 'contactStatusId' || field === 'status') { // Generic status fallback
            return (
                <Select mode="multiple" placeholder="Select Statuses" allowClear showSearch optionFilterProp="children">
                    {contactStatuses?.map(s => <Option key={s.id} value={s.id}>{s.statusName}</Option>)}
                </Select>
            );
        }
        if (field === 'leadStatus') {
            return (
                <Select mode="multiple" placeholder="Select Lead Status" allowClear showSearch optionFilterProp="children">
                    {leadStatuses?.map(s => <Option key={s.id} value={s.name}>{s.name}</Option>)}
                </Select>
            );
        }
        if (field === 'leadSourceId' || field === 'contactSourceId') {
            return (
                <Select mode="multiple" placeholder="Select Sources" allowClear showSearch optionFilterProp="children">
                    {leadSources?.map(s => <Option key={s.id} value={s.id}>{s.name}</Option>)}
                </Select>
            );
        }
        if (field === 'agent_id' || (definition.source === 'agents' && field === 'id')) {
            return (
                <Select mode="multiple" placeholder="Select Agents" allowClear showSearch optionFilterProp="children">
                    {agentList?.map(a => <Option key={a.id} value={a.id}>{a.companyName || a.name}</Option>)}
                </Select>
            );
        }
        if (field === 'university_id' || field === 'universityIds' || field === 'universities') {
            return (
                <Select mode="multiple" placeholder="Select Universities" allowClear showSearch optionFilterProp="children">
                    {universities?.map(u => <Option key={u.id} value={u.id}>{u.universityName || u.name}</Option>)}
                </Select>
            );
        }
        if (field === 'major_id' || field === 'major') {
            return (
                <Select mode="multiple" placeholder="Select Majors" allowClear showSearch optionFilterProp="children">
                    {majors?.map(m => <Option key={m.id} value={m.id}>{m.name}</Option>)}
                </Select>
            );
        }
        if (field === 'interest') {
            return (
                <Select mode="multiple" placeholder="Select Interests" allowClear showSearch optionFilterProp="children">
                    {interests?.map(i => <Option key={i.id} value={i.id}>{i.name}</Option>)}
                </Select>
            );
        }
        if (field === 'agent_commission_paid') {
            return (
                <Select placeholder="Select Status">
                    <Option value="yes">Yes</Option>
                    <Option value="no">No</Option>
                </Select>
            );
        }
        if (field === 'scholarship') {
            return (
                <Select placeholder="Select Scholarship">
                    <Option value={0}>0%</Option>
                    <Option value={25}>25%</Option>
                    <Option value={50}>50%</Option>
                    <Option value={100}>100%</Option>
                </Select>
            );
        }
        if (field === 'degree') {
            return (
                <Select mode="multiple" placeholder="Select Degree" loading={degreeLoading} allowClear showSearch optionFilterProp="children">
                    {degrees?.map(d => (
                        <Option key={d.id} value={d.name}>{d.name}</Option>
                    ))}
                </Select>
            );
        }
        if (field === 'country') {
            return (
                <Select mode="multiple" placeholder="Select Country" showSearch optionFilterProp="children">
                    {COUNTRIES.map(c => <Option key={c.value} value={c.value}>{c.label}</Option>)}
                </Select>
            );
        }
        if (field === 'nationality') {
            return (
                <Select mode="multiple" placeholder="Select Nationality" showSearch optionFilterProp="children">
                    {NATIONALITIES.map(n => <Option key={n.value} value={n.value}>{n.label}</Option>)}
                </Select>
            );
        }
        if (field === 'created_at') {
            return <DatePicker style={{ width: '100%' }} />;
        }

        return <Input placeholder="Enter value" />;
    }

    return (
        <Modal
            title={isEditMode ? t('analytics.editAnalytics') : t('analytics.createSmartBuilder')}
            open={visible}
            onOk={handleOk}
            onCancel={onClose}
            confirmLoading={loading}
            okText={isEditMode ? t('common.update') : t('common.create')}
            width={750}
        >
            <Form
                form={form}
                layout="vertical"
                initialValues={{
                    dataSource: 'deals',
                    chartType: 'bar',
                    aggregation: 'count',
                    timePeriod: 'all_time'
                }}
            >
                <div className="grid grid-cols-2 gap-4">
                    <Form.Item
                        name="name"
                        label={t('analytics.reportName')}
                        rules={[{ required: true, message: t('analytics.reportNamePlaceholder') }]}
                    >
                        <Input placeholder={t('analytics.reportNamePlaceholder')} />
                    </Form.Item>

                    <Form.Item
                        name="dataSource"
                        label={t('analytics.dataSource')}
                        rules={[{ required: true, message: t('analytics.dataSource') }]}
                    >
                        <Select onChange={(val) => handleDefinitionChange('source', val)}>
                            {getSourceOptions().map(opt => (
                                <Option key={opt.value} value={opt.value}>{opt.label}</Option>
                            ))}
                        </Select>
                    </Form.Item>
                </div>

                <div className="p-4 bg-gray-50 rounded mb-4 border border-gray-100">
                    <h4 className="mb-3 font-semibold text-gray-700">{t('analytics.analysisConfiguration')}</h4>

                    <div className="grid grid-cols-3 gap-4">
                        <Form.Item
                            name="groupBy"
                            label={t('analytics.groupBy')}
                            rules={[{ required: true, message: t('analytics.selectDimensionPlaceholder') }]}
                            className="col-span-1"
                        >
                            <Select
                                placeholder={t('analytics.selectDimensionPlaceholder')}
                                showSearch
                                optionFilterProp="children"
                                onChange={(val) => handleDefinitionChange('dimensions', [val])}
                            >
                                {dimensionOptions.map(opt => (
                                    <Option key={opt.value} value={opt.value}>{opt.label}</Option>
                                ))}
                            </Select>
                        </Form.Item>

                        <Form.Item
                            name="aggregation"
                            label={t('analytics.metric')}
                            rules={[{ required: true, message: t('analytics.selectMetricPlaceholder') }]}
                            className="col-span-1"
                        >
                            <Select placeholder={t('analytics.selectMetricPlaceholder')} onChange={(val) => handleDefinitionChange('metrics', [val])}>
                                {metricOptions.map(opt => (
                                    <Option key={opt.value} value={opt.value}>{opt.label}</Option>
                                ))}
                            </Select>
                        </Form.Item>

                        <Form.Item
                            name="timePeriod"
                            label={t('analytics.timePeriod')}
                            rules={[{ required: true, message: t('analytics.selectTimePeriodPlaceholder') }]}
                            className="col-span-1"
                        >
                            <Select placeholder={t('analytics.selectTimePeriodPlaceholder')}>
                                <Option value="all_time">{t('analytics.allTime')}</Option>
                                <Option value="this_month">{t('analytics.thisMonth')}</Option>
                                <Option value="last_month">{t('analytics.lastMonth')}</Option>
                                <Option value="this_year">{t('analytics.thisYear')}</Option>
                                <Option value="last_year">{t('analytics.lastYear')}</Option>
                            </Select>
                        </Form.Item>
                    </div>
                </div>

                <div className="p-4 bg-gray-50 rounded mb-4 border border-gray-100">
                    <h4 className="mb-3 font-semibold text-gray-700">{t('analytics.filtersConditions')}</h4>
                    <p className="text-xs text-gray-500 mb-2">{t('analytics.filtersHelp')}</p>

                    <Form.List name="filters">
                        {(fields, { add, remove }) => (
                            <>
                                {fields.map(({ key, name, ...restField }) => (
                                    <Space key={key} style={{ display: 'flex', marginBottom: 8 }} align="baseline">
                                        <Form.Item
                                            {...restField}
                                            name={[name, 'field']}
                                            rules={[{ required: true, message: t('common.missingField') }]}
                                            style={{ width: 200 }}
                                        >
                                            <Select
                                                placeholder={t('analytics.field')}
                                                showSearch
                                                optionFilterProp="children"
                                                onChange={() => {
                                                    // Clear value when field changes to avoid type mismatch
                                                    form.setFieldValue(['filters', name, 'value'], undefined);
                                                }}
                                            >
                                                {dimensionOptions.map(opt => (
                                                    <Option key={opt.value} value={opt.value}>{opt.label}</Option>
                                                ))}
                                            </Select>
                                        </Form.Item>

                                        <Form.Item
                                            noStyle
                                            shouldUpdate={(prev, curr) => prev.filters?.[name]?.field !== curr.filters?.[name]?.field}
                                        >
                                            {({ getFieldValue }) => {
                                                const fieldName = getFieldValue(['filters', name, 'field']);
                                                return (
                                                    <Form.Item
                                                        {...restField}
                                                        name={[name, 'value']}
                                                        rules={[{ required: true, message: t('common.missingValue') }]}
                                                        style={{ width: 250 }}
                                                    >
                                                        {renderFilterInput(fieldName)}
                                                    </Form.Item>
                                                );
                                            }}
                                        </Form.Item>
                                        <MinusCircleOutlined onClick={() => remove(name)} />
                                    </Space>
                                ))}
                                <Form.Item>
                                    <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>
                                        {t('analytics.addFilter')}
                                    </Button>
                                </Form.Item>
                            </>
                        )}
                    </Form.List>
                </div>

                <Form.Item
                    name="chartType"
                    label={t('analytics.visualization')}
                    rules={[{ required: true, message: t('analytics.selectChartTypePlaceholder') }]}
                >
                    <Select placeholder={t('analytics.selectChartTypePlaceholder')}>
                        <Option value="bar">{t('analytics.barChart')}</Option>
                        <Option value="pie">{t('analytics.pieChart')}</Option>
                        <Option value="line">{t('analytics.lineChart')}</Option>
                        <Option value="area">{t('analytics.areaChart')}</Option>
                    </Select>
                </Form.Item>

                <Form.Item name="description" label={t('common.description')}>
                    <TextArea rows={2} placeholder={t('analytics.optionalDescription')} />
                </Form.Item>

            </Form>
        </Modal>
    );
};

export default CreateAnalyticsModal;
